import React, { Fragment, useEffect, useState } from "react";
import Classes from "../css/Modal.module.css";
import axiosInstance from '../../../../utility/axios-instance';
import { TextField, Autocomplete } from '@mui/material';
import Spinner from '../../../../components/spinner';
import { AutoCompleteOptions, CustomerAddressCreateResponse, ShippingPopUpProps } from "@/types";

const ShippingPopUp: React.FC<ShippingPopUpProps> = (props) => {
    const [states, setStates] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const {
        shippingModal,
        hideShippingPop,
        showCustomerDetail,
        addressArray,
        setAddressArray,
        addressType,
        num,
        setNum,
        pincode,
        setPincode,
        state, 
        setState,
        setSelectedShippingAddress,
        setSelectedBillingAddress
    } = props;

    const [selectedState, setSelectedState] = useState<string>(state?? '');
    const [cities, setCities] = useState<string[]>([]);

    async function handlePincodeChange(event: React.ChangeEvent<HTMLInputElement>) {
        const limit = 6;
        const enteredPincode = event.target.value.slice(0, limit)
        setPincode(enteredPincode);
        try {
            const response = await fetch(`https://api.postalpincode.in/pincode/${enteredPincode}`);
            const data = await response.json();

            if (data[0]?.Status === 'Success') {
                const extractedState = data[0]?.PostOffice[0]?.State;
                setState(extractedState);
            }
        } catch (err) {
            console.log(err);
        }
    }

    const handleNumChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const limit = 10;
        setNum(event.target.value.slice(0, limit));
    };


    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        setLoading(true);
        event.preventDefault();
        hideShippingPop();

        const target = event.target as HTMLFormElement;
        axiosInstance
            .post<CustomerAddressCreateResponse>(`/user/admin/${showCustomerDetail?.id}/address`, {
                address1: target.address1.value,
                address2: target.address2.value,
                city: target.city.value,
                province: target.state.value,
                zip: target.pincode.value,
                country: target.country.value,
                firstName: target.first.value,
                lastName: target.last.value ? target.last.value : undefined,
                nick_name: target.nick_name.value ?? undefined,
                phone: `+91${target.phone.value}`,
                addressType: addressType,
                ...(addressType !== "shipping" && { gstNumber: target.gst.value ? target.gst.value : undefined }),
            })
            .then((res) => {
                const createdAddress = res.data.data;
                setLoading(false);
                setAddressArray([...addressArray, res.data.data])
                const modifiedAddress = { 
                    ...createdAddress, 
                    addressLine1: createdAddress.address1, 
                    addressLine2: createdAddress.address2, 
                    pincode: createdAddress.zip, 
                    state: createdAddress.province, 
                    phoneNumber: createdAddress.phone, 
                }
                if (res.data.data.addressType === 'shipping') {
                    setSelectedShippingAddress(modifiedAddress)
                } else {
                    setSelectedBillingAddress(modifiedAddress)
                }
            })
            .catch((err) => {
                setLoading(false);
                alert(err?.response?.data?.message || 'Something went wrong');
                console.log(err);
            });
    }

    const handleMinLengthPinCode = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (event.target.value) {
            const pincode = /^\d{6}$/;
            if (event.target.value.match(pincode)) {
                const pinErrMsg = document.getElementById('pinErrMsg');
                if (pinErrMsg) {
                    pinErrMsg.style.display = 'none';
                }
                const addAddressButton = document.getElementById('addAddress');
                if (addAddressButton) {
                    (addAddressButton as HTMLButtonElement).disabled = false;
                }
            } else {
                const addAddressButton = document.getElementById('addAddress');
                if (addAddressButton) {
                    (addAddressButton as HTMLButtonElement).disabled = true;
                }
                const pinErrMsg = document.getElementById('pinErrMsg');
                if (pinErrMsg) {
                    pinErrMsg.style.display = 'block';
                }
            }
        } else {
            const pinErrMsg = document.getElementById('pinErrMsg');
            if (pinErrMsg) {
                pinErrMsg.style.display = 'none';
            }
            const addAddressButton = document.getElementById('addAddress');
            if (addAddressButton) {
                (addAddressButton as HTMLButtonElement).disabled = false;
            }
        }
    }

    const handleMinLengthPhoneNo = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (event.target.value) {
            const phoneNo = /^\d{10}$/;
            if (event.target.value.match(phoneNo)) {
                const phoneErrMsg = document.getElementById('phoneErrMsg');
                if (phoneErrMsg) {
                    phoneErrMsg.style.display = 'none';
                }
                const addAddressButton = document.getElementById('addAddress');
                if (addAddressButton) {
                    (addAddressButton as HTMLButtonElement).disabled = false;
                }
            } else {
                const addAddressButton = document.getElementById('addAddress');
                if (addAddressButton) {
                    (addAddressButton as HTMLButtonElement).disabled = true;
                }
                const phoneErrMsg = document.getElementById('phoneErrMsg');
                if (phoneErrMsg) {
                    phoneErrMsg.style.display = 'block';
                }
            }
        } else {
            const phoneErrMsg = document.getElementById('phoneErrMsg');
            if (phoneErrMsg) {
                phoneErrMsg.style.display = 'none';
            }
            const addAddressButton = document.getElementById('addAddress');
            if (addAddressButton) {
                (addAddressButton as HTMLButtonElement).disabled = false;
            }
        }
    }

    useEffect(() => {
        axiosInstance
            .get('/pincode/getAllStates')
            .then((response) => {
                setStates(response.data.data);
            })
    }, [])

    useEffect(()=> {
        if (selectedState) {
            axiosInstance
            .get(`/pincode/getAllCities?state=${selectedState}`)
            .then((response) => {
                setCities(response.data.data);
            })
        }
    },[selectedState]);

    const handleValidGst = (event: React.FocusEvent<HTMLInputElement>) => {
        let regex = new RegExp(/^[0-9]{2}[a-zA-Z]{5}[0-9]{4}[a-zA-Z]{1}[1-9A-Za-z]{1}[zZ]{1}[0-9a-zA-Z]{1}$/);

        if (event.target.value == null) {
            return "false";
        }

        if (regex.test(event.target.value) === true) {
            const addAddressButton = document.getElementById('addAddress');
            if (addAddressButton) {
                (addAddressButton as HTMLButtonElement).disabled = false;
            }
            const isValidGst = document.getElementById('isValidGst');
            if (isValidGst) {
                isValidGst.style.display = 'none';
            }
        } else {
            if (event.target.value === '') {
                const addAddressButton = document.getElementById('addAddress');
                if (addAddressButton) {
                    (addAddressButton as HTMLButtonElement).disabled = false;
                }
                const isValidGst = document.getElementById('isValidGst');
                if (isValidGst) {
                    isValidGst.style.display = 'none';
                }
            } else {
                const addAddressButton = document.getElementById('addAddress');
                if (addAddressButton) {
                    (addAddressButton as HTMLButtonElement).disabled = true;
                }
                const isValidGst = document.getElementById('isValidGst');
                if (isValidGst) {
                    isValidGst.style.display = 'block';
                }
            }
        }
    }

    return (
        <Fragment>
            {shippingModal && (
                <div className={Classes.modal} style={{ bottom: '10%', top: '3%' }}>
                    <div className={Classes.BoxContent}>
                        <p>Add New Address</p>
                    </div>

                    <form method="post" onSubmit={handleSubmit} id='formData'>
                        <div className={Classes.AddressPopUp}>
                            <div style={{ marginTop: 10 }}>
                                <TextField
                                    id='country'
                                    name='country'
                                    label='Country / Origin'
                                    value='India'
                                    autoComplete='off'
                                    sx={{ width: '100%', "& label": { top: "0%", fontSize: 14 }, "& .MuiOutlinedInput-input": { padding: '6px 10px' } }}
                                    disabled={true}
                                />
                            </div>

                            <div style={{ display: 'flex', marginTop: 10, justifyContent: 'space-between' }}>
                                <div style={{ width: '48%' }}>
                                    <TextField
                                        required={true}
                                        id='first'
                                        size="small"
                                        name='first'
                                        label='First Name'
                                        autoComplete='off'
                                        placeholder='Enter Your First Name'
                                        sx={{ width: '100%' }}
                                    />
                                </div>

                                <div style={{ width: '48%' }}>
                                    <TextField
                                        id='last'
                                        name='last'
                                        size="small"
                                        label='Last Name'
                                        autoComplete='off'
                                        placeholder='Enter Your Last Name'
                                        sx={{ width: '100%' }}
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'flex', marginTop: 10, justifyContent: 'space-between' }}>
                                <div style={{ width: '48%' }}>
                                    <TextField
                                        required={false}
                                        id='nick_name'
                                        size="small"
                                        name='nick_name'
                                        label='Nick Name'
                                        autoComplete='off'
                                        placeholder='Enter Your Nick Name'
                                        sx={{ width: '100%' }}
                                    />
                                </div>
                            </div>

                            <div style={{ marginTop: '10px' }}>
                                <TextField
                                    required={true}
                                    id='address1'
                                    name='address1'
                                    label='Address 1'
                                    size="small"
                                    autoComplete='off'
                                    placeholder='Enter Your Address'
                                    sx={{ width: '100%' }}
                                />
                            </div>

                            <div style={{ marginTop: '10px' }}>
                                <TextField
                                    id='address2'
                                    name='address2'
                                    label='Address 2'
                                    autoComplete='off'
                                    placeholder='Apartment,Suite,etc'
                                    sx={{ width: '100%' }}
                                />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
                                <div style={{ width: '48%' }}>
                                    <Autocomplete
                                        // freeSolo
                                        disablePortal
                                        size="small"
                                        id="state"
                                        options={states}
                                        ListboxProps={{ sx: { fontSize: 14 } }}
                                        sx={{ width: '100%' }}
                                        defaultValue={state}
                                        onChange={(e, newValue) => setSelectedState(newValue ?? '')}
                                        key={state}
                                        // onChange={(event, newValue) => { setSelectedState(newValue.id) }}
                                        renderInput={(params) => <TextField {...params} label="States" required />}
                                    />
                                </div>

                                <div style={{ width: '48%' }}>
                                    <TextField
                                        type="number"
                                        required={true}
                                        id='phone'
                                        name='phone'
                                        label='Phone'
                                        size="small"
                                        autoComplete='off'
                                        placeholder='Enter Your Phone Number'
                                        inputProps={{ maxLength: 10 }}
                                        sx={{ width: '100%' }}
                                        value={num}
                                        onBlur={(e) => handleMinLengthPhoneNo(e)}
                                        onChange={(e) => handleNumChange(e)}
                                    />

                                    <div
                                        className={Classes.PhoneErrorMsg}
                                        id='phoneErrMsg'
                                    >
                                        <p>Please enter a valid 10 digit mobile number</p>
                                    </div>
                                </div>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
                                <div style={{ width: '48%' }}>
                                    <Autocomplete
                                        freeSolo
                                        disablePortal
                                        id="city"
                                        size="small"
                                        options={cities}
                                        disabled={!selectedState ? true: false}
                                        renderInput={(params) => <TextField {...params} label="City"/>}
                                    />
                                </div>

                                <div style={{ width: '48%' }}>
                                    <TextField
                                        type="number"
                                        required={true}
                                        id='pincode'
                                        size="small"
                                        name='pincode'
                                        label='Pincode'
                                        autoComplete='off'
                                        placeholder='Enter Your Pincode'
                                        inputProps={{ maxLength: 6, minLength: 6 }}
                                        sx={{ width: '100%' }}
                                        value={pincode}
                                        onBlur={(e) => handleMinLengthPinCode(e)}
                                        onChange={handlePincodeChange}
                                    />

                                    <div
                                        className={Classes.PhoneErrorMsg}
                                        id='pinErrMsg'
                                        style={{ fontSize: '14px' }}
                                    >
                                        <p>Please enter a valid 6 digit pincode</p>
                                    </div>
                                </div>
                            </div>
                            {addressType !== 'shipping' && (

                            <div style={{ display: 'flex', marginTop: 10, justifyContent: 'space-between' }}>
                                <div style={{ width: '48%' }}>
                                    <TextField
                                        id='gst'
                                        name='gst'
                                        label='GST Number'
                                        size="small"
                                        autoComplete='off'
                                        placeholder='Enter GST Number'
                                        sx={{ width: '100%' }}
                                        onBlur={handleValidGst}

                                    />
                                    <div
                                        id='isValidGst'
                                        className={Classes.IsValidGst}
                                    >
                                        <p>Please fill valid Gst Number</p>
                                    </div>
                                </div>
                            </div>
                            )}
                        </div>

                        <div className={Classes.DialogBtnBox} style={{ bottom: "10%" }}>
                            <button
                                type="button"
                                className={Classes.UpdateSalesUserOfCustomer}
                                onClick={hideShippingPop}
                            >
                                CANCEL
                            </button>

                            <button
                                id='addAddress'
                                type="submit"
                                className={Classes.UpdateSalesUserOfCustomer}
                            >
                                ADD
                            </button>
                        </div>
                    </form>

                    {loading ? <Spinner /> : null}
                </div>
            )}
        </Fragment>
    );
}


export default ShippingPopUp;